import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { AuthProvider, ErrorBoundary, useAuth } from '@forbiz/web-kit'

import { isWebRole } from './roles.js'
import { Shell } from './Shell.js'
import { DevAccountSwitcher } from './components/DevAccountSwitcher.js'
import { CardPage } from './pages/CardPage.js'
import { InviteAcceptPage } from './pages/InviteAcceptPage.js'
import { LoginPage } from './pages/LoginPage.js'
import { SignupPage } from './pages/SignupPage.js'
import { ForgotPasswordPage } from './pages/ForgotPasswordPage.js'
import { ResetPasswordPage } from './pages/ResetPasswordPage.js'
import { InvoicesPage } from './pages/InvoicesPage.js'
import { InvoiceDetailPage } from './pages/InvoiceDetailPage.js'
import { HomePage } from './pages/HomePage.js'
import { CatalogPage } from './pages/CatalogPage.js'
import { ProductDetailPage } from './pages/ProductDetailPage.js'
import { OrderCreatePage } from './pages/OrderCreatePage.js'
import { OrderDetailPage } from './pages/OrderDetailPage.js'
import { PaymentFailPage, PaymentSuccessPage } from './pages/PaymentResultPage.js'
import { OrdersPage } from './pages/OrdersPage.js'
import { SettlementPage } from './pages/SettlementPage.js'
import { StatsPage } from './pages/StatsPage.js'
import { QuickOrderSettingsPage } from './pages/QuickOrderSettingsPage.js'
import { MyCompanyPage } from './pages/MyCompanyPage.js'
import { ProductCurationPage } from './pages/ProductCurationPage.js'
import { NoticesPage } from './pages/NoticesPage.js'
import { NoticeDetailPage } from './pages/NoticeDetailPage.js'
import { GuidePage } from './pages/GuidePage.js'
import { DigitalWreathPage } from './pages/DigitalWreathPage.js'
import { ReferralPage } from './pages/ReferralPage.js'

/**
 * 기업 관리자·담당자와 화환 업체 담당자가 쓰는 앱.
 * 운영자 화면은 apps/admin에 따로 있다.
 *
 * 인증이 필요한 경로는 Shell(내비 포함) 아래에 두고 RequireAuth로 감싼다.
 * /login·/invite는 로그인 전에도 접근하는 공개 경로다.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return null
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />
  // 운영자 등 이 앱의 역할이 아니면 화면을 그리지 않는다. 소속(companyId) 전제 페이지가
  // 터지는 대신 안내를 보여주고 운영자 앱으로 안내한다. 프로덕션은 admin과 쿠키를 공유해
  // /admin에 로그인한 운영자가 '/'로 넘어와도 여기로 걸린다.
  if (!isWebRole(user.role)) return <WrongAppNotice />
  return <>{children}</>
}

/** 잘못된 앱에 들어온 로그인 사용자(운영자)에게 보이는 안내. 크래시 대신 길을 안내한다. */
function WrongAppNotice() {
  const { user, logout } = useAuth()
  return (
    <div style={{ maxWidth: 420, margin: '15vh auto', padding: '0 24px', textAlign: 'center' }}>
      <h1 style={{ fontSize: 20, marginBottom: 12 }}>포비즈</h1>
      <p style={{ marginBottom: 8 }}>
        <b>{user?.name} 님</b>은 운영자 계정입니다.
      </p>
      <p style={{ color: '#666', marginBottom: 24, lineHeight: 1.6 }}>
        이 화면은 기업·업체 담당자용입니다. 운영자 화면으로 이동해 주세요.
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
        <button data-variant="primary" onClick={() => window.location.assign('/admin/')}>
          운영자 화면으로 이동
        </button>
        <button data-variant="secondary" onClick={() => void logout()}>
          로그아웃
        </button>
      </div>
    </div>
  )
}

function RedirectIfAuthed({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return null
  // 로그인 성공 후 이 래퍼가 리다이렉트를 이긴다 — 그래서 복귀 경로(state.from)를
  // 여기서 함께 존중한다(BIZ-30). 세션 만료로 튕겨 온 자리로 돌려보낸다.
  if (user) {
    const from = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from
    // 기본 착지는 홈 — 가입/로그인 직후 이 가드가 페이지의 navigate('/')와 경합해
    // 신규 사용자를 빈 '주문 내역'으로 보내던 레이스의 원인이었다. 업체 계정은 홈이
    // 스스로 /orders로 보낸다(HomePage의 비기업 리다이렉트).
    const dest = from?.pathname ? `${from.pathname}${from.search ?? ''}` : '/'
    return <Navigate to={dest} replace />
  }
  return <>{children}</>
}

export function App() {
  // 라우트가 바뀌면 바운더리를 리셋한다 — 에러 난 화면에서 다른 곳으로 이동하면 회복되게.
  const location = useLocation()
  return (
    <AuthProvider>
      {/* dev 전용 계정 스위처 — 역할 전환 원탭. 프로덕션 번들에선 조건이 false로 접혀 제거된다. */}
      {import.meta.env.DEV ? <DevAccountSwitcher /> : null}
      <ErrorBoundary resetKeys={[location.pathname]}>
      <Routes>
        <Route
          path="/login"
          element={
            <RedirectIfAuthed>
              <LoginPage />
            </RedirectIfAuthed>
          }
        />
        {/* 셀프 회원가입(BIZ-67). 초대 없이 주체별 계정을 만드는 공개 경로. */}
        <Route
          path="/signup"
          element={
            <RedirectIfAuthed>
              <SignupPage />
            </RedirectIfAuthed>
          }
        />
        {/* 초대 수락(BIZ-3). 로그인 전에도 접근하는 공개 경로. */}
        <Route path="/invite" element={<InviteAcceptPage />} />
        {/* 수신자 디지털 화환 카드(BIZ-86) — 무인증 공개 페이지 */}
        <Route path="/card/:token" element={<CardPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* 인증 영역. Shell이 내비를 그리고 Outlet에 화면을 끼운다. */}
        <Route
          element={
            <RequireAuth>
              <Shell />
            </RequireAuth>
          }
        >
          <Route path="/" element={<HomePage />} />
          <Route path="/products" element={<CatalogPage />} />
          <Route path="/products/:id" element={<ProductDetailPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/settlement" element={<SettlementPage />} />
          <Route path="/stats" element={<StatsPage />} />
          <Route path="/orders/new" element={<OrderCreatePage />} />
          <Route path="/orders/:id/edit" element={<OrderCreatePage />} />
          <Route path="/orders/:id" element={<OrderDetailPage />} />
          <Route path="/payments/success" element={<PaymentSuccessPage />} />
          <Route path="/payments/fail" element={<PaymentFailPage />} />
          <Route path="/invoices" element={<InvoicesPage />} />
          <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
          <Route path="/notices" element={<NoticesPage />} />
          <Route path="/notices/:id" element={<NoticeDetailPage />} />
          <Route path="/guide" element={<GuidePage />} />
          <Route path="/digital-wreath" element={<DigitalWreathPage />} />
          <Route path="/referral" element={<ReferralPage />} />
          <Route path="/settings/products" element={<ProductCurationPage />} />
          <Route path="/settings/senders" element={<QuickOrderSettingsPage />} />
          <Route path="/settings/company" element={<MyCompanyPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/orders" replace />} />
      </Routes>
      </ErrorBoundary>
    </AuthProvider>
  )
}
